import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CidadeService } from '../cidade.service';
import { NotificationService } from '../../../core/notification.service';
import { ESTADOS_BR } from '../../../shared/utils/brasil.utils';

function slugify(v: string): string {
  return v
    .toString()
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

@Component({
  standalone: false,
  selector: 'app-cidade-form',
  template: `
    <div class="container mx-auto px-4 py-6 max-w-2xl">
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-800">{{ isEdit ? '✏️ Editar Cidade' : '➕ Nova Cidade' }}</h1>
        <p class="text-gray-600 mt-1">{{ isEdit ? 'Atualize os dados da cidade' : 'Cadastre uma nova cidade na plataforma' }}</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Nome da Cidade <span class="text-red-500">*</span></label>
            <input formControlName="nome" type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Piracicaba" />
            <div *ngIf="form.controls['nome'].errors?.['required'] && form.controls['nome'].touched"
              class="text-sm text-red-600 mt-1">Nome é obrigatório</div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Estado (UF) <span class="text-red-500">*</span></label>
              <select formControlName="estado"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Selecione</option>
                <option *ngFor="let uf of estados" [value]="uf">{{ uf }}</option>
              </select>
              <div *ngIf="form.controls['estado'].errors?.['required'] && form.controls['estado'].touched"
                class="text-sm text-red-600 mt-1">Estado é obrigatório</div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
              <input formControlName="slug" type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="piracicaba" />
              <p class="text-xs text-gray-500 mt-1">Gerado automaticamente</p>
            </div>
          </div>

          <div>
            <label class="flex items-center gap-3 cursor-pointer p-2 hover:bg-gray-50 rounded">
              <input type="checkbox" formControlName="ativo" class="w-4 h-4 text-blue-600 rounded" />
              <span class="text-sm font-medium text-gray-700">✓ Cidade ativa (visível para seleção)</span>
            </label>
          </div>

          <div class="flex gap-3 pt-4 border-t">
            <button type="submit" [disabled]="form.invalid || saving"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium">
              {{ saving ? '⏳ Salvando...' : (isEdit ? '💾 Atualizar' : '➕ Criar Cidade') }}
            </button>
            <button type="button" (click)="cancel()"
              class="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-medium">
              ✕ Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  `
})
export class CidadeFormComponent implements OnInit {
  form!: FormGroup;
  isEdit = false;
  saving = false;
  estados = ESTADOS_BR;

  constructor(
    private fb: FormBuilder,
    private svc: CidadeService,
    private router: Router,
    private route: ActivatedRoute,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.params['id'];
    this.isEdit = !!id;

    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(100)]],
      estado: ['', [Validators.required, Validators.maxLength(2)]],
      slug: ['', [Validators.maxLength(120)]],
      ativo: [true]
    });

    this.form.controls['nome'].valueChanges.subscribe(v => {
      if (!this.form.controls['slug'].dirty) {
        this.form.controls['slug'].setValue(slugify(v || ''), { emitEvent: false });
      }
    });

    if (this.isEdit && id) {
      this.svc.get(id).subscribe({
        next: (c: any) => {
          this.form.patchValue({
            nome: c.nome || '',
            estado: c.estado || '',
            slug: c.slug || '',
            ativo: c.ativo !== false
          });
        },
        error: (err: any) => {
          console.error('[CidadeForm] erro ao carregar cidade', err);
          this.notify.showError('Erro ao carregar cidade');
        }
      });
    }
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    this.saving = true;
    const id = this.route.snapshot.params['id'];
    const op = id
      ? this.svc.update(id, this.form.value)
      : this.svc.create(this.form.value);

    op.subscribe({
      next: () => {
        this.saving = false;
        this.notify.showSuccess(id ? 'Cidade atualizada com sucesso' : 'Cidade criada com sucesso');
        this.router.navigate(['/admin/cidades']);
      },
      error: (err: any) => {
        this.saving = false;
        console.error('[CidadeForm] erro ao salvar', err);
        
        // Tratamento específico para erro 409 (slug duplicado)
        if (err?.status === 409 || err?.error?.code === 'SLUG_DUPLICADO') {
          this.form.get('slug')?.setErrors({ 'server': 'Este slug já existe. Escolha outro.' });
          this.notify.showError('❌ Slug duplicado! Escolha um slug único para a cidade.');
        } else if (err?.error?.message?.includes('slug')) {
          this.form.get('slug')?.setErrors({ 'server': err?.error?.message });
          this.notify.showError('❌ Erro com o slug: ' + err?.error?.message);
        } else {
          this.notify.showError('Erro ao salvar cidade: ' + (err?.error?.message || err?.statusText || 'Erro desconhecido'));
        }
      }
    });
  }

  cancel(): void { this.router.navigate(['/admin/cidades']); }
}
